import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';
import {ifDefined} from 'lit-html/directives/if-defined';
import {live} from 'lit-html/directives/live';
import {classMap} from 'lit-html/directives/class-map';

import TemplateRegistry from '../template/template-registry';
import {AppExtensions, CartItemProperties, PriceType, ThemeConfig, ThemeCurrency} from '../const';
import { optionClassMap, valueSync} from '../template/template-util.js';
import {formatMoney} from '../money';
import Util from '../util';


class QuantityDiscountController {

    constructor(onChange, getOption) {
        this.onChange = onChange;
        this.getOption = getOption;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render

        this.quantity = 1;
        this.quantityEl = null;
        this.quantityElOption = false; // flag to show a quantity el inside the option
        this.quantityId = ''; // to combine quantity discounts with similar products
        this.quantityDiscount = ''; // quantity levels
        this.quantityMin = null; // function-quantity-validate min check
        this.discount = 0;
        this.discountType = PriceType.ABSOLUTE;
        this.quantityUnit = 1;
        this._linkedQuantityOption = null;  // ref to quantity option if present
        this._deferredLink = false;         // flag when quantity controller not ready yet
    }

    _findQuantityOption() {
        const qOpt = this.getOption('quantity');
        if (qOpt && qOpt.type === 'quantity') return qOpt;
        return null;
    }

    onValidate(option) {

        // NEW: dynamic quantityId generation on add to cart
        if(option.params.quantityId){

            let quantityId = option.params.quantityId;

            // NEW: custom variable support
            if(quantityId.indexOf('{random}') !== -1){
                // generate random id (1-1000)
                quantityId = quantityId.replace('{random}', String(Math.floor( Math.random() * 1000 + 1)));
            }
            if(quantityId.indexOf('{product}') !== -1){
                // use product id as key (useful when config is applied to many different products)
                quantityId = quantityId.replace('{product}', String(this.config.product.id));
            }
            if(quantityId.indexOf('{variant}') !== -1){
                // variant ID is set on form - we access value (could be provided by render function)
                quantityId = quantityId.replace('{variant}', String(this.config.productForm['id'].value));
            }
            /* OVERKILL options hash - once requested by a customer - then they enabled options reset on add to cart - new image upload would always create a new options hash
            if(quantityId.indexOf('{options}') !== -1){

                let optionsJson = "{}";
                // hacky find the productOption class by product id
                // IMPROVEMENT: use a web component for productOptions.js - so we could find the parent element OR pass a getValues function
                for (let i = 0; i < window.Cloudlift.options.App.products.length; i++) {
                    const productOption = window.Cloudlift.options.App.products[i];
                    // form match is the correct productOptions instance
                    if(productOption._productForm === this.config.productForm){
                        let optionValues = productOption.getValues();
                        // delete quantity discount value (not relevant)
                        delete optionValues[option.name];
                        // we could apply more cleaning here
                        // serialize all values
                        optionsJson = JSON.stringify(optionValues);
                        break;
                    }
                }

                const hashCode = (s) => {
                    let h;
                    for(let i = 0; i < s.length; i++)
                        h = Math.imul(31, h) + s.charCodeAt(i) | 0;
                    return h;
                }

                // use hashcode from json to create uniqueness
                quantityId = quantityId.replace('{options}', String(hashCode(optionsJson)));
            } */

            // set quantityId (rendered right after validation)
            this.quantityId = quantityId;
        }

        return true;
    }

    handleChange(e, option) { // IMPORTANT: to pass option and not use the reference of the ['checkbox'] = (option, ..)   in editorMode the option reference can change

        // update quantity based on selection
        this.quantity = parseInt(option.value);

        this.syncEl();

        this.selectDiscount(option);

        // reverse sync: update linked quantity option when a tier is clicked
        if (this._linkedQuantityOption?._controller) {
            const qCtrl = this._linkedQuantityOption._controller;
            qCtrl.quantity = this.quantity;
            this._linkedQuantityOption.value = String(this.quantity);
            qCtrl.syncEl();
        }

        // trigger change
        this.onChange(option);

    }

    selectDiscount(option) {

        // save currently selected discount
        if(option.values){
            for (let i = 0; i < option.values.length; i++) {
                const optionValue = option.values[i];
                if(optionValue.value === option.value && optionValue._discount){
                    this.discount = optionValue._discount;
                    this.discountType = optionValue.priceType;
                    return; // discount found
                }
            }
        }

        // NO discount found
        this.discount = 0;
    }

    syncEl() {
        if(this.quantityEl) this.quantityEl.value = this.quantity;
    }

    syncValue(option, fromQuantity) {
        if (fromQuantity !== undefined) {
            this.quantity = fromQuantity;
        } else if (this.quantityEl) {
            this.quantity = parseInt(this.quantityEl.value) || 1;
        }

        // reset selected discount optionValue
        option.value = '';

        // reset current discount
        this.discount = 0;

        if(option.values) {
            for (let i = 0; i < option.values.length; i++) {
                const optionValue = option.values[i];

                // required quantity
                let quantity = parseInt(optionValue.value);

                if (this.quantity >= quantity) {

                   if(optionValue._discount) {
                       // make sure discount is bigger
                       if (optionValue._discount > this.discount) {
                           // select value on UI
                           option.value = optionValue.value;
                       }
                   } else {
                       // HACKY: empty discount - allow selection without bigger check
                       option.value = optionValue.value;
                   }

                }
            }
        }

        // select discount value based on option value
        this.selectDiscount(option);

    }

    quantityDiscountText(option, quantity, discount, discountType, config, quantitySuffix = '', priceQuantity) {

        // optionQuantity: tiers only determine discount %, price is per single item unless priceQuantity is given
        const vars = this.calcQuantityDiscountVars(option, option.params.optionQuantity ? (priceQuantity || 1) : quantity, discount, discountType, config);

        let template = (option.params.discountTemplate || '{price} {compare} {discount}');

        // empty discount > clean compare/discount variables
        if(!discount){
            // regex to remove {compareP} {compare} {discountP} {discount}
            template = template.replace(new RegExp('{compareP}|{compare}|{discountP}|{discount}', 'g'), '').trim();
        }

        // unit text (useful in combination with optionQuantity to show a custom unit e.g. m2)
        let unit = '';
        if(option.params.unitQuantity){
            // wrap in span
            unit = `<span class="_unit">${option.params.unitQuantity}</span>`;
        }
        // round decimal quantities to two decimals
        if(quantity % 1 !== 0){
            quantity = quantity.toFixed(2);
        }

        return `<span class="_quantity">${option.params.textDiscountBuy || 'Buy:'} ${quantity}${unit}${quantitySuffix}</span>${template}`
            .replace(new RegExp('{compareP}', 'g'), `<span class="_compare">${formatMoney(vars.price * 100, ThemeCurrency.moneyFormat)}</span>`)
            .replace(new RegExp('{compare}', 'g'), `<span class="_compare">${formatMoney(vars.total * 100, ThemeCurrency.moneyFormat)}</span>`)
            .replace(new RegExp('{priceP}', 'g'), `<span class="_price">${formatMoney(vars.productFinal * 100, ThemeCurrency.moneyFormat)}</span>`)
            .replace(new RegExp('{price}', 'g'), `<span class="_price">${formatMoney(vars.totalFinal * 100, ThemeCurrency.moneyFormat)}</span>`)
            .replace(new RegExp('{discountP}', 'g'), `<span class="_save">${vars.productSave}</span>`)
            .replace(new RegExp('{discount}', 'g'), `<span class="_save">${vars.totalSave}</span>`);
    }

    optionValueDiscountText(option, optionValue, config) {

        let quantity = parseInt(optionValue.value);

        let quantitySuffix = option.params.quantitySuffix || '+'; // not currently in UI

        let index = option.values.indexOf(optionValue);

        // NEW: check if next quantity is just one more - then remove the quantity suffix
        if(option.values.length > index + 1){
            let nextQuantity = parseInt(option.values[index + 1].value);
            if(nextQuantity === quantity + 1){
                quantitySuffix = '';
            }
        }

        return this.quantityDiscountText(option, quantity, optionValue._discount, optionValue.priceType, config, quantitySuffix);
    }

    calcQuantityDiscountVars(option, quantity, discount, discountType, config) {

        // NEW: priceVariant are not supported in the discount we have to subtract the separate variantTotal to show the same price as in cart for this product
        let price = config.price.final - config.price.variantTotal;

        // NEW: BUNDLE extension - bundled products are also discounted in the Shopify checkout
        // config.price.variantTotal contains all add-ons (bundled or not) - we need to only add the bundle add-ons
        if(config.price.bundle.length){
            for (const bundleProduct of config.price.bundle) {
                if(bundleProduct.id !== '0'){ // main price already calculated
                    price += bundleProduct.price * bundleProduct.quantity;
                }
            }
        }

        let total = quantity * price;

        let productFinal;
        let productSave;
        let totalFinal;
        let totalSave;

        // NEW: discount can be empty (just showing the price without discount)
        if(discount){
            if(discountType === PriceType.PERCENTAGE){
                // subtract the percentage per unit floored to whole cents - SAME MATH AS Shopify checkout (verified), NOT total * percentage
                productFinal = price - Math.floor(Math.round(price * 100) * discount / 100) / 100;
                // total price multiply by quantity
                totalFinal = productFinal * quantity;
                // save percentage text (same for both)
                productSave = `${option.params.textDiscountSave || 'Save:'} ${discount}%`;
                totalSave = productSave;
            }
            // ABSOLUTE default
            else {

                // dynamic currency convert - discounts are not prices: round 2 decimal instead of full dollar ceil
                // SAME CODE AS IN function-quantity-discount run.js
                if(ThemeCurrency.currencyConvert && ThemeCurrency.currencyFactor !== 1){
                    discount = Math.round(discount * ThemeCurrency.currencyFactor * 100) / 100;
                }

                // avoid giving more discount than the product price (would not make sense - even full price is weird - would make it free)
                let discountValue = Math.min(discount, price);
                // subtract the discount
                productFinal = price - discountValue;

                productSave = '-' + formatMoney(discountValue * 100, ThemeCurrency.moneyFormat);

                // discount per quantity
                let discountTotal = discountValue * quantity;
                // subtract the discount
                totalFinal = total - discountTotal;

                totalSave = '-' + formatMoney(discountTotal * 100, ThemeCurrency.moneyFormat);
            }
        } else {
            productFinal = price;
            productSave = '';
            totalFinal = total;
            totalSave = '';
        }

        // return all calculated values
        return { price, total, productFinal, productSave, totalFinal, totalSave };
    }

    updatePriceElements(option, config) {

        // INFO: similar logic as in product-options.js for data-live-price

        // previous found element => check if still in page (some themes completely replace parts of the page)
        if(this._priceElList && this._priceElList.length){
            for (let i = 0; i < this._priceElList.length; i++) {
                if(!Util.isInPage(this._priceElList[i])){
                    delete this._priceElList; // delete to resolve again
                    break; // full list is deleted
                }
            }
        }

        if(!this._priceElList || this._priceElList.length === 0) {
            this._priceElList = document.querySelectorAll('[data-live-price-quantity="' + config.product?.id + '"], [data-live-price-quantity-single="' + config.product?.id + '"]');
        }

        if(this._priceElList.length){

            // get the final quantity (for the exact current quantity)
            const vars = this.calcQuantityDiscountVars(option, this.quantity, this.discount, this.discountType, config);

            this._priceElList.forEach((el) => {

                // single quantity price via:  data-live-price-quantity-single       DEFAULT: show total price for selected quantity
                const varsValue = el.dataset.livePriceQuantitySingle !== undefined ? 'productFinal' : 'totalFinal';

                let priceValue = vars[varsValue];

                /* INFO: variantTotal is excluded from the quantity price (same as in cart)
                if(varsValue === 'productFinal'){
                    priceValue += config.price.variantTotal;
                } else {
                    // if we would want to show the final total + variant options we would need to loop over config.price.variants and add the price per variant.quantity
                    // also requires access to the correct variantPrice - could be mapped in productOptions.js: this.price.variants.push(optionValue.priceVariant);
                } */

                // format the price
                let priceTotal = formatMoney(priceValue * 100, ThemeCurrency.moneyFormat);

                // hacky: we have to avoid the ThemeConfig.productPriceObserver logic to update the price again (compares the price from data-live-format attribute)
                if(el.dataset.liveFormat){
                    el.dataset.liveFormat = priceTotal;
                }
                el.innerHTML = priceTotal;
            });
        }

        /* NEW: re-sync on change if not matching (multiple discount options with conditions) */
        if(this.quantityEl && this.quantityEl.value !== String(this.quantity)){
            this.syncEl(option);
        }

    }

    init(option) {

        let quantityDiscountLevels = [];

        if(option.values){
            // move the price to a hidden field
            for (let i = 0; i < option.values.length; i++) {
                const optionValue = option.values[i];

                let quantityValue = parseInt(optionValue.value);

                // IMPORTANT: custom ._price for priceType (set in product-options.js)
                if (option.params.discountFormula) {
                    // used as "discount" variable in formula evaluation (editor mode repeated init)
                    optionValue._discountVar = optionValue._discountVar || optionValue._price || optionValue.price;
                    optionValue._discount = 0;  // placeholder — evaluated in update()
                } else if(optionValue.price || optionValue._price){
                    optionValue._discount = optionValue._price || optionValue.price;

                    if(!isNaN(quantityValue)){
                        // store each level with its discount value + type
                        quantityDiscountLevels.push(quantityValue + ":" + optionValue._discount + (optionValue.priceType === PriceType.PERCENTAGE ? '%' : ''));
                    }
                }

                // avoid selection to change total
                optionValue.price = 0;
                optionValue._price = 0;

                // IMPORTANT: outside of optionValue.price check - could be set without a discount
                // store lowest quantity as min
                if(ThemeConfig.checkoutQuantityValidate && !isNaN(quantityValue)){
                    this.quantityMin = this.quantityMin === null ? quantityValue : Math.min(this.quantityMin, quantityValue);
                }
            }
        }

        this.quantityId = this.config.uuid; // default combine all from same config

        // store discount levels on controller > added to product on save
        this.quantityDiscount = quantityDiscountLevels.join(',');

        // only init once
        if(!this.quantityEl){
            // try to link to a quantity option first
            const qOpt = this._findQuantityOption();

            if (qOpt) {
                if (qOpt._controller) {
                    // quantity option already initialized — link up
                    this._linkToQuantityOption(qOpt, option);
                } else {
                    // quantity option exists but not initialized yet (init ordering)
                    this._deferredLink = true;
                    // do NOT create own quantity input — avoid UI flash
                }
            } else {
                // no quantity option or not visible — normal behavior
                this._initNormalQuantityEl(option);
            }
        }

        // NEW: pre selected quantity       // avoid in editor mode init is called on every render
        if(option.params.selectedQuantity && !this.config.editorMode){
            // wait for quantity element to be ready
            setTimeout(() => {
                this.quantity = parseInt(option.params.selectedQuantity);
                this.syncEl(option);
                this.syncValue(option);
                this.onChange(option);
                //this.handleChange(null, option);
            });
        }

    }

    _initNormalQuantityEl(option) {
        const selector = '[name=quantity]';
        this.quantityEl = this.config.productForm.querySelector(selector) || document.querySelector(selector);

        // hidden quantity field > remove to add our own
        if(this.quantityEl && this.quantityEl.type === 'hidden'){
            // remove from theme
            this.quantityEl.parentNode.removeChild(this.quantityEl);
            this.quantityEl = null;
        }

        if(this.quantityEl && !option.params.quantityInput){ // NEW: allow to always force a quantity input (setting hidden)

            this.syncValue(option);

            this.quantityEl.addEventListener('change', () => {

                // IMPORTANT: skip render and value sync when hidden see logic: (multiple discount options with conditions)
                if(option.visible){

                    this.syncValue(option);
                    // change to re-render options
                    this.onChange(option);
                }

            })
        }
        // not found show our own element
        else if(!this.quantityElOption) {

            // enable own element
            this.quantityElOption = true;

            const initQuantityEl = (e) => {
                this.config.productForm.removeEventListener('options:rendered', initQuantityEl);

                this.quantityEl = document.querySelector('#' + option.id + '-quantity');
                this.quantityEl.value = '1'; // init with one
                this.syncValue(option);
            };
            // wait for options render to complete
            this.config.productForm.addEventListener('options:rendered', initQuantityEl);
        }
    }

    _linkToQuantityOption(qOpt, option) {
        this._linkedQuantityOption = qOpt;
        this._deferredLink = false;
        this.quantityElOption = false; // suppress own quantity input

        const qCtrl = qOpt._controller;
        this.quantity = qCtrl.quantity;
        this.syncValue(option, this.quantity);

        // resolve quantityEl from quantity option's rendered input
        const resolveEl = () => {
            this.config.productForm.removeEventListener('options:rendered', resolveEl);
            this.quantityEl = document.querySelector('#' + qOpt.id + '-quantity');
        };

        // element may already exist
        const existingEl = document.querySelector('#' + qOpt.id + '-quantity');
        if (existingEl) {
            this.quantityEl = existingEl;
        } else {
            this.config.productForm.addEventListener('options:rendered', resolveEl);
        }
    }

    get effectiveQuantity() {
        return this.quantityUnit * this.quantity;
    }

    _basePrice(config) {
        let price = config.price.final - config.price.variantTotal;
        if (config.price.bundle.length) {
            for (const bp of config.price.bundle) {
                if (bp.id !== '0') price += bp.price * bp.quantity;
            }
        }
        return price;
    }

    _evalFormula(optionValue, option, basePrice, config) {
        // Cache compiled function on option (same formula for all tiers)
        if (!option._discountFunction) {
            let discountFormula = option.params.discountFormula;

            // expression only (no variable declaration & no return)
            if(discountFormula.indexOf('return') === -1){
                discountFormula = 'return ( ' + discountFormula + ' )';
            }

            try {
                // validate: options.quantity is not available, use the "quantity" variable instead
                if (config.editorMode && discountFormula.indexOf('options.quantity') !== -1) {
                    throw new Error('use the "quantity" variable instead of "options.quantity"');
                }

                option._discountFunction = Function(
                    '"use strict";return (function(option, value, price, options, quantity, discount){' + discountFormula + '})'
                )();
            } catch (e) {
                if (config.editorMode) {
                    option.errors = option.errors || [];
                    if (option.errors.indexOf(e.message) === -1) option.errors.push(e.message);
                }
                return 0;
            }
        }

        try {
            // quantity is the value defined on the tier level
            const quantity = parseInt(optionValue.value);
            // price discount configured value for each level
            const discount = parseFloat(optionValue._discountVar);
            // price is overwritten with single quantity basePrice
            let result = option._discountFunction(
                option, optionValue.value, basePrice, config._priceFunctionUtil, quantity, discount
            );
            // support percentage format in formula result
            if (typeof result === 'string' && result.indexOf('%') !== -1) {
                optionValue.priceType = PriceType.PERCENTAGE;
                return Math.max(0, parseFloat(result) || 0);
            }
            optionValue.priceType = PriceType.ABSOLUTE;
            return Math.max(0, result || 0);
        } catch (e) {
            if (config.editorMode) {
                option.errors = option.errors || [];
                if (option.errors.indexOf(e.message) === -1) option.errors.push(e.message);
            }
            return 0;
        }
    }

    update(option, config) {
        // 0. linked quantity option sync
        if (this._deferredLink) {
            // re-attempt linking now that quantity option may have initialized
            const qOpt = this._findQuantityOption();
            if (qOpt && qOpt._controller) {
                this._linkToQuantityOption(qOpt, option);
            }
        }

        if (this._linkedQuantityOption) {
            const qOpt = this._linkedQuantityOption;
            if (qOpt.visible !== false && qOpt._controller) {
                // sync quantity from linked option
                this.syncValue(option, qOpt._controller.quantity);
            } else {
                // linked option became hidden — fall back to normal behavior
                this._linkedQuantityOption = null;
                this.quantityEl = null;
                this._initNormalQuantityEl(option);
            }
        }

        // 1. optionQuantity — read linked option's value as per-item unit (multiplied by quantity to calculate effective quantity for discount tiers)
        if (option.params.optionQuantity) {
            let optionQuantity = this.getOption(option.params.optionQuantity);
            if (optionQuantity) {
                // special case `dimension` option type use .area as quantity unit
                if(optionQuantity.type === 'dimension'){
                    this.quantityUnit = parseFloat(optionQuantity._controller?.area) || 1;
                } else {
                    this.quantityUnit = parseFloat(optionQuantity?.value) || 1;
                }
            }
        }

        // 2. formula evaluation — all tiers
        let hasFormula = !!option.params.discountFormula;
        if (hasFormula) {
            let basePrice = this._basePrice(config);
            for (const ov of (option.values || [])) {
                ov._discount = this._evalFormula(ov, option, basePrice, config);
            }
        }

        // 3. re-sync tier + re-serialize when dynamic state changed
        if (this.quantityUnit !== 1 || hasFormula) {
            this._syncTier(option);
            this._serializeDiscountLevels(option);
        }
    }

    _syncTier(option) {
        option.value = '';
        this.discount = 0;

        let eq = this.effectiveQuantity;

        if (option.values) {
            for (let i = 0; i < option.values.length; i++) {
                const optionValue = option.values[i];
                let quantity = parseInt(optionValue.value);

                if (eq >= quantity) {
                    if (optionValue._discount) {
                        if (optionValue._discount > this.discount) {
                            option.value = optionValue.value;
                        }
                    } else {
                        option.value = optionValue.value;
                    }
                }
            }
        }

        this.selectDiscount(option);
    }

    _serializeDiscountLevels(option) {
        let levels = [];
        if (option.values) {
            for (let i = 0; i < option.values.length; i++) {
                const optionValue = option.values[i];
                let quantityValue = parseInt(optionValue.value);
                if (optionValue._discount && !isNaN(quantityValue)) {
                    levels.push(quantityValue + ':' + optionValue._discount
                        + (optionValue.priceType === PriceType.PERCENTAGE ? '%' : ''));
                }
            }
        }
        this.quantityDiscount = levels.join(',');
    }
}


TemplateRegistry.templates['quantity-discount'] = (option, config, onChange, onErrorClear, getOption) => {

    // extension not enabled
    if(!AppExtensions.QUANTITY){
        return html``;
    }

    // init flag when no controller yet
    let init = option._controller === undefined;

    // get or create quantity discount controller
    option._controller = option._controller || new QuantityDiscountController(onChange, getOption);

    option._controller.config = config;
    option._controller.option = option;

    // INFO: editor mode always update the values (required to init the discount attributes on the values)
    if(init || config.editorMode){
        option._controller.init(option);
    }

    // NEW: single call handles optionQuantity + discountFormula + tier re-sync
    option._controller.update(option, config);

    // NEW: external price element updates supports (easier to implement here than to extend the product-options.js)
    option._controller.updatePriceElements(option, config);

    // default radio single selection (swatchToggle function required to support de-selection with radio)
    return html`
            ${repeat(option.values || [],  (optionValue,index) => html`
                <label class="cl-po--radio-item" @click=${option.params.optionQuantity ? (e) => {
                    e.preventDefault();
                    const optionEl = document.querySelector(`[data-option="${option.params.optionQuantity}"]`);
                    if (optionEl) {
                        optionEl.scrollIntoView({behavior: 'smooth', block: 'center'});
                        const input = optionEl.querySelector('input:not([type="hidden"]), textarea, select');
                        if (input) setTimeout(() => input.focus({preventScroll: true}), 300);
                    }
                } : undefined}>
                    <input type="radio" class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}-${index}" .value=${optionValue.value} @change=${(e) => valueSync(e, option, (option) => option._controller.handleChange(e, option) )}
                           .checked=${live(option.value == optionValue.value)} ?required=${option.required} name="_${option.name}" form=${ifDefined(config.productFormLink)}>
                    <div class="cl-po--discount-text">${unsafeHTML(option._controller.optionValueDiscountText(option, optionValue, config))}</div>
                </label>
            `)}

            ${option.params.discountLine !== false && option._controller.discount && (option.params.optionQuantity
                ? (option.params.discountTemplate || '{price}').includes('{price}') && option._controller.quantity > 1
                : option._controller.effectiveQuantity !== parseInt(option.value)) ? html`
                <label class="cl-po--radio-item cl-po--discount-text-custom">
                    <input type="radio" class="cl-po--input ${classMap(optionClassMap(option))}" style="opacity:0">
                    <div class="cl-po--discount-text">${unsafeHTML(option._controller.quantityDiscountText(option, option._controller.effectiveQuantity, option._controller.discount, option._controller.discountType, config, '', option._controller.quantity))}</div>
                </label>
            ` : null}
           
            ${option._controller.quantityElOption ? html`
                <div class="cl-po--quantity-wrap cl-po--quantity-discount-el">
                    <span class="cl-po--quantity-btn" @click=${(e) => { option._controller.quantity--; option._controller.syncEl(option); option._controller.syncValue(option); onChange(option); } }>-</span>
                    <input class="cl-po--input cl-po--quantity" id="${option.id}-quantity" name="quantity" form=${ifDefined(config.productFormLink)} @change=${(e) => {option._controller.syncValue(option); onChange(option); } }>
                    <span class="cl-po--quantity-btn" @click=${(e) => { option._controller.quantity++; option._controller.syncEl(option); option._controller.syncValue(option); onChange(option); } }>+</span>
                </div>
            ` : null}
            
            <input type="hidden" name="properties[${CartItemProperties.QUANTITY_ID}]" value=${option._controller.quantityId} form=${ifDefined(config.productFormLink)}>
            <input type="hidden" name="properties[${CartItemProperties.QUANTITY_DISCOUNT}]" value=${option._controller.quantityDiscount} form=${ifDefined(config.productFormLink)}>

            ${option._controller.quantityMin !== null ? html`
                <input type="hidden" name="properties[${CartItemProperties.QUANTITY_MIN}]" value=${option._controller.quantityMin} form=${ifDefined(config.productFormLink)}>
            ` : null}

            ${option._controller.quantityUnit !== 1 ? html`
                <input type="hidden" name="properties[${CartItemProperties.QUANTITY_UNIT}]" value=${option._controller.quantityUnit} form=${ifDefined(config.productFormLink)}>
            ` : null}
            
            ${option.params.discountText ? html`
                   <input type="hidden" name="properties[${CartItemProperties.QUANTITY_TEXT}]" value=${option.params.discountText} form=${ifDefined(config.productFormLink)}>
               ` : null}
        `
};
